import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

const UNION_ERROR_MESSAGES = {
  authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  account_not_active: '현재 계정 상태에서는 유니온을 등록하거나 가입할 수 없습니다.',
  verified_union_required: 'BlablaLink에서 유니온 소속이 확인된 게임 프로필이 필요합니다.',
  current_union_membership_exists: '이미 진행 중이거나 승인된 유니온 소속 정보가 있습니다.',
  union_already_registered: '이 유니온은 이미 등록됐거나 승인 대기 중입니다.',
  union_not_found: '가입할 유니온을 찾을 수 없습니다.',
  union_not_active: '현재 활성 상태인 유니온이 아닙니다.',
  game_profile_union_mismatch: '인증된 게임 프로필의 유니온과 가입 대상이 일치하지 않습니다.',
  community_type_required: '갤러리인지 채널인지 커뮤니티 유형을 선택해주세요.',
};

const APP_CONTEXT_CACHE_TTL = 10_000;
let appContextCache = null;
let appContextCacheAt = 0;
let appContextCacheUserId = '';
let appContextRequest = null;
let appContextRequestUserId = '';
let appContextGeneration = 0;

function requireSupabaseClient() {
  if (!hasSupabaseConfig || !supabase) {
    throw new Error('Supabase 환경 변수가 설정되지 않았습니다.');
  }
  return supabase;
}

function createUnionError(error, fallbackMessage) {
  const rawMessage = String(error?.message || '');
  const matchedCode = Object.keys(UNION_ERROR_MESSAGES).find((code) => rawMessage.includes(code));
  const unionError = new Error(
    UNION_ERROR_MESSAGES[matchedCode] || fallbackMessage || '유니온 정보를 처리하지 못했습니다.',
  );
  unionError.code = matchedCode || error?.code || 'unknown_error';
  return unionError;
}

function normalizeContext(data) {
  const value = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
  return {
    profile: value.profile || null,
    membership: value.membership || null,
    union: value.union || null,
    matchingUnion: value.matchingUnion || value.matching_union || null,
    isPlatformAdmin: Boolean(value.isPlatformAdmin ?? value.is_platform_admin),
  };
}

async function getCurrentSessionUserId(client) {
  const { data, error } = await client.auth.getSession();
  const userId = String(data?.session?.user?.id || '');
  if (error || !userId) throw createUnionError({ message: 'authentication_required' });
  return userId;
}

async function requestUnionRegistrationState(client) {
  const { data, error } = await client.rpc('get_current_user_app_context');
  if (error) throw createUnionError(error, '계정과 유니온 상태를 불러오지 못했습니다.');
  return normalizeContext(data);
}

export function invalidateUnionRegistrationStateCache() {
  appContextGeneration += 1;
  appContextCache = null;
  appContextCacheAt = 0;
  appContextCacheUserId = '';
  appContextRequest = null;
  appContextRequestUserId = '';
}

export async function loadUnionRegistrationState({ force = false } = {}) {
  const client = requireSupabaseClient();
  const userId = await getCurrentSessionUserId(client);
  const now = Date.now();

  if ((appContextCacheUserId && appContextCacheUserId !== userId)
    || (appContextRequestUserId && appContextRequestUserId !== userId)) {
    invalidateUnionRegistrationStateCache();
  }

  if (appContextRequest && appContextRequestUserId === userId) {
    return appContextRequest;
  }

  if (!force
    && appContextCache
    && appContextCacheUserId === userId
    && now - appContextCacheAt < APP_CONTEXT_CACHE_TTL) {
    return appContextCache;
  }

  const generation = appContextGeneration + 1;
  appContextGeneration = generation;
  const request = requestUnionRegistrationState(client)
    .then((result) => {
      if (appContextGeneration === generation) {
        appContextCache = result;
        appContextCacheAt = Date.now();
        appContextCacheUserId = userId;
      }
      return result;
    })
    .finally(() => {
      if (appContextRequest === request) {
        appContextRequest = null;
        appContextRequestUserId = '';
      }
    });

  appContextRequest = request;
  appContextRequestUserId = userId;
  return request;
}

export async function requestUnionRegistration(communityType) {
  const normalizedCommunityType = String(communityType || '').trim();
  if (!['gallery', 'channel'].includes(normalizedCommunityType)) {
    throw createUnionError({ message: 'community_type_required' });
  }

  const client = requireSupabaseClient();
  const { data, error } = await client.rpc('request_union_registration', {
    p_community_type: normalizedCommunityType,
  });
  if (error) throw createUnionError(error, '유니온 등록 신청을 완료하지 못했습니다.');
  const result = Array.isArray(data) ? data[0] : data;
  if (!result?.union_id || result.registration_status !== 'pending') {
    throw createUnionError(null, '유니온 등록 신청 결과를 확인하지 못했습니다.');
  }
  invalidateUnionRegistrationStateCache();
  return result;
}

export async function requestUnionMembership(unionId) {
  const normalizedUnionId = String(unionId || '').trim();
  if (!normalizedUnionId) throw createUnionError({ message: 'union_not_found' });

  const client = requireSupabaseClient();
  const { data, error } = await client.rpc('request_union_membership', {
    target_union_id: normalizedUnionId,
  });
  if (error) throw createUnionError(error, '유니온 자동 가입을 완료하지 못했습니다.');
  const result = Array.isArray(data) ? data[0] : data;
  if (!result?.membership_id || result.membership_status !== 'active') {
    throw createUnionError(null, '유니온 자동 가입 결과를 확인하지 못했습니다.');
  }
  invalidateUnionRegistrationStateCache();
  return result;
}
